import { useEffect } from "react";
import { publicMediaSrc, toConfiguredAbsoluteUrl } from "../config/api";
import { deliveryHref } from "./deliveryHref";
import deliveriesShareImage from "../assets/deliveries-hero.jpg";

const DEFAULT_DOCUMENT_TITLE = "Steel, Hardware & Industrial Supplies Malaysia";
const DEFAULT_DOCUMENT_DESCRIPTION =
  "Steel, hardware, fasteners, roofing, paint and industrial products for contractors across Malaysia. Request a quote today.";
const COLLECTION_JSON_LD_ID = "deliveries-collection-jsonld";
const BREADCRUMB_JSON_LD_ID = "deliveries-breadcrumb-jsonld";
const ITEMLIST_JSON_LD_ID = "deliveries-itemlist-jsonld";

function cleanText(value) {
  const text = String(value ?? "").trim();
  if (!text || text === "undefined" || text === "null") return "";
  return text;
}

function upsertMeta(attr, key, content) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!content) {
    if (el && el.dataset.deliveriesSeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  el.dataset.deliveriesSeo = "true";
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el && el.dataset.deliveriesSeo === "true") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  el.dataset.deliveriesSeo = "true";
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function prune(value) {
  if (Array.isArray(value)) {
    const next = value.map(prune).filter((item) => item !== undefined);
    return next.length ? next : undefined;
  }
  if (value && typeof value === "object") {
    const next = {};
    Object.keys(value).forEach((key) => {
      const item = prune(value[key]);
      if (item !== undefined && item !== "") next[key] = item;
    });
    return Object.keys(next).length ? next : undefined;
  }
  if (value === undefined || value === null || value === "") return undefined;
  return value;
}

function clearDeliveriesSeo() {
  document.head.querySelectorAll("[data-deliveries-seo='true']").forEach((el) => el.remove());
  upsertJsonLd(COLLECTION_JSON_LD_ID, null);
  upsertJsonLd(BREADCRUMB_JSON_LD_ID, null);
  upsertJsonLd(ITEMLIST_JSON_LD_ID, null);
}

export function deliveriesPageSeo(contact) {
  const siteName = cleanText(contact?.siteName);
  if (!siteName) return null;
  return {
    siteName,
    title: `Industrial Material Deliveries Malaysia | ${siteName}`,
    description: `View recent steel, hardware and industrial product deliveries completed by ${siteName} for project sites across Malaysia.`,
    imageAlt: `Industrial material delivery arranged by ${siteName}`,
    itemListName: `Recent deliveries by ${siteName}`,
  };
}

function applyNeutralMetadata(canonicalUrl) {
  document.title = DEFAULT_DOCUMENT_TITLE;
  upsertMeta("name", "description", DEFAULT_DOCUMENT_DESCRIPTION);
  upsertLink("canonical", canonicalUrl);
  upsertMeta("name", "robots", "");
  upsertJsonLd(COLLECTION_JSON_LD_ID, null);
  upsertJsonLd(BREADCRUMB_JSON_LD_ID, null);
  upsertJsonLd(ITEMLIST_JSON_LD_ID, null);
}

function DeliveriesSeo({
  contact,
  contactStatus = "loading",
  deliveriesReady = false,
  items = [],
  itemCount = 0,
  page = 1,
  pageSize = 9,
}) {
  useEffect(() => {
    const canonicalUrl = toConfiguredAbsoluteUrl("/deliveries");
    const homeUrl = toConfiguredAbsoluteUrl("/");
    const websiteId = `${String(homeUrl || "/").replace(/\/$/, "")}/#website`;
    const organizationId = `${String(homeUrl || "/").replace(/\/$/, "")}/#organization`;

    if (contactStatus === "loading") {
      applyNeutralMetadata(canonicalUrl);
      return () => {
        document.title = DEFAULT_DOCUMENT_TITLE;
        clearDeliveriesSeo();
      };
    }

    const seo = deliveriesPageSeo(contact);
    if (!seo) {
      applyNeutralMetadata(canonicalUrl);
      return () => {
        document.title = DEFAULT_DOCUMENT_TITLE;
        clearDeliveriesSeo();
      };
    }

    const imageUrl = toConfiguredAbsoluteUrl(deliveriesShareImage);

    document.title = seo.title;
    upsertMeta("name", "description", seo.description);
    upsertLink("canonical", canonicalUrl);
    upsertMeta("name", "robots", "");
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", imageUrl);
    upsertMeta("property", "og:image:alt", imageUrl ? seo.imageAlt : "");
    upsertMeta("property", "og:site_name", seo.siteName);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", imageUrl);
    upsertMeta("name", "twitter:image:alt", imageUrl ? seo.imageAlt : "");

    upsertJsonLd(
      COLLECTION_JSON_LD_ID,
      prune({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": canonicalUrl ? `${canonicalUrl}#webpage` : undefined,
        url: canonicalUrl || undefined,
        name: seo.title,
        description: seo.description,
        isPartOf: { "@id": websiteId },
        about: { "@id": organizationId },
        breadcrumb: canonicalUrl ? { "@id": `${canonicalUrl}#breadcrumb` } : undefined,
      })
    );

    upsertJsonLd(
      BREADCRUMB_JSON_LD_ID,
      prune({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": canonicalUrl ? `${canonicalUrl}#breadcrumb` : undefined,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: homeUrl },
          { "@type": "ListItem", position: 2, name: "Deliveries", item: canonicalUrl },
        ],
      })
    );

    const listItems = deliveriesReady
      ? (Array.isArray(items) ? items : []).filter((item) => cleanText(item?.title))
      : [];

    if (listItems.length) {
      const start = Math.max(0, (Number(page) || 1) - 1) * (Number(pageSize) || 9);
      upsertJsonLd(
        ITEMLIST_JSON_LD_ID,
        prune({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": canonicalUrl ? `${canonicalUrl}#delivery-list` : undefined,
          name: seo.itemListName,
          numberOfItems: itemCount,
          itemListElement: listItems.map((item, index) => {
            const imagePath = publicMediaSrc(item?.images?.[0]?.url);
            const href = deliveryHref(item);
            return {
              "@type": "ListItem",
              position: start + index + 1,
              name: cleanText(item.title),
              url: href && href !== "/deliveries" ? toConfiguredAbsoluteUrl(href) : undefined,
              image: imagePath ? toConfiguredAbsoluteUrl(imagePath) : undefined,
            };
          }),
        })
      );
    } else {
      upsertJsonLd(ITEMLIST_JSON_LD_ID, null);
    }

    return () => {
      document.title = DEFAULT_DOCUMENT_TITLE;
      clearDeliveriesSeo();
    };
  }, [contact, contactStatus, deliveriesReady, items, itemCount, page, pageSize]);

  return null;
}

export default DeliveriesSeo;
